import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { languageNames } from '../i18n/translations';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Globe } from '@phosphor-icons/react';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Left Panel with Background Image */}
      <div className="login-left" style={{
        backgroundImage: 'url(/login-bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative'
      }}>
        {/* Content removed - image only */}
      </div>

      {/* Right Panel */}
      <div className="login-right">
        <div className="login-form">
          {/* Language Switcher */}
          <div className="flex justify-end mb-8">
            <div className="lang-switcher">
              {Object.entries(languageNames).map(([code, name]) => (
                <button
                  key={code}
                  onClick={() => changeLanguage(code)}
                  className={`lang-btn ${language === code ? 'active' : ''}`}
                  data-testid={`lang-${code}`}
                >
                  {code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold tracking-tight text-[#09090B]">
              {t('auth.loginTo')} ARCO Swiss
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-[#FFEBEE] border border-[#E32636] text-[#E32636] px-4 py-3 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                {t('auth.email')}
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-swiss h-12"
                placeholder="email@azienda.ch"
                required
                data-testid="login-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                {t('auth.password')}
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-swiss h-12"
                placeholder="********"
                required
                data-testid="login-password"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#002FA7] hover:bg-[#002075] text-white font-medium rounded-sm"
              data-testid="login-submit"
            >
              {loading ? t('common.loading') : t('auth.loginButton')}
            </Button>
          </form>

          <div className="mt-8 pt-8 border-t border-[#E4E4E7] text-center">
            <p className="text-xs text-[#A1A1AA] uppercase tracking-wider">
              Archiviazione Conforme a Norma di Legge in Svizzera
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
